/** Spec/dim colors for PDF and UI — darker 600-level tones. */
const FIXED: Record<string, string> = {
  place: "#0D9488",
  qty: "#DC2626",
  w: "#2563EB",
  h: "#0891B2",
  l: "#7C3AED",
};

const PALETTE = [
  "#D97706",
  "#EA580C",
  "#DB2777",
  "#C026D3",
  "#4F46E5",
  "#2563EB",
  "#0284C7",
  "#059669",
  "#16A34A",
  "#65A30D",
  "#CA8A04",
  "#E11D48",
  "#9333EA",
  "#1D4ED8",
  "#0E7490",
  "#0F766E",
  "#A16207",
  "#BE123C",
  "#6D28D9",
  "#0369A1",
  "#15803D",
  "#B45309",
  "#B91C1C",
  "#4D7C0F",
];

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function normalizeSpecKey(name: string | null | undefined): string {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "_");
}

export function getSpecColor(key: string | null | undefined, storedColor?: string | null): string {
  const norm = normalizeSpecKey(key);
  if (norm === "place") return FIXED.place;
  if (norm === "qty" || norm === "quantity") return FIXED.qty;
  if (norm === "w" || norm === "width") return FIXED.w;
  if (norm === "h" || norm === "height") return FIXED.h;
  if (norm === "l" || norm === "length") return FIXED.l;

  const idx = hashString(norm) % PALETTE.length;
  const base = PALETTE[idx] || "#475569";
  const stored = String(storedColor || "").trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(stored)) {
    return darkenHexColor(stored);
  }
  return darkenHexColor(base);
}

/** Ensure each spec on a line item gets a different dark color (no duplicates). */
export function assignDistinctSpecColors<T extends { label: string; color: string }>(
  entries: T[]
): T[] {
  if (entries.length <= 1) return entries;
  const used = new Set<string>();
  return entries.map((entry) => {
    const norm = normalizeSpecKey(entry.label);
    let attempt = 0;
    let color = getSpecColor(entry.label, entry.color);
    while (used.has(color.toUpperCase()) && attempt < PALETTE.length) {
      const idx = (hashString(norm) + attempt + 1) % PALETTE.length;
      color = darkenHexColor(PALETTE[idx] || "#475569");
      attempt++;
    }
    used.add(color.toUpperCase());
    return { ...entry, color };
  });
}

/** Pull pale stored/palette hex down to a darker, still-readable shade. */
export function darkenHexColor(hex: string): string {
  const m = /^#?([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/.exec(hex.trim());
  if (!m) return hex;
  const r = parseInt(m[1], 16) / 255;
  const g = parseInt(m[2], 16) / 255;
  const b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      default:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  const targetL = Math.min(l, 0.42);
  const targetS = Math.min(Math.max(s, 0.55), 0.85);
  return hslToHex(h, targetS, targetL);
}

/** @deprecated Use darkenHexColor. Kept so older imports still darken existing colors. */
export function lightenHexColor(hex: string): string {
  return darkenHexColor(hex);
}

function hslToHex(h: number, s: number, l: number): string {
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let r: number;
  let g: number;
  let b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (x: number) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

export function dimLabelColor(label: "W" | "H" | "L" | "Qty"): string {
  if (label === "W") return FIXED.w;
  if (label === "H") return FIXED.h;
  if (label === "L") return FIXED.l;
  return FIXED.qty;
}

export function chunkPairs<T>(items: T[]): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += 2) {
    rows.push(items.slice(i, i + 2));
  }
  return rows;
}
